import React from 'react'
import type { Listing } from '@adc/sdk/types'
import * as constants from '@adc/constants'
import type { primitives } from '@adc/blueprint'
import { formatPhoneNumber } from 'common/utils/formatters'
import { CombinedListingType } from 'common/context/listing'
import { useContactModal } from 'common/modules/propertyDetails/useContactModal'
import { getContactAgentInfo } from 'common/utils/listing'
import moment from 'moment'
import {
  Button,
  hooks,
  ScrollSpy,
  Text,
  useApplicationContext,
  useApplicationModals,
} from '@adc/ui-adc'
import classnames from 'classnames/bind'
import { useMarketingAnalysisCard } from 'common/hooks/useSelectMarketAnalysisCard'
// @ts-ignore no types
import styles from './index.scss'

const cx = classnames.bind(styles)
type AuctionUser = primitives.AuctionUser

export enum AlternateItemType {
  MapItem,
  StreetViewItem,
  CustomItem,
}

export const getOpenHouses = (listing: Listing) => {
  const openHouses = [] as string[]
  const todaysDate = moment()

  listing.open_houses?.forEach(openHouse => {
    const time = moment(openHouse?.local_date)
    if (time > todaysDate) openHouses.push(moment(time).format('MMM DD'))
  })

  return openHouses.length ? openHouses.join(', ') : ''
}

interface BaseProps {
  featureFlags: any
  listing: Listing
  user?: AuctionUser
}

interface AuthModalProps {
  pushOnboardingQueueItem: (args: any[]) => void
  toggleAuthModal: () => void
}

interface ChicletPropsWithAuth extends BaseProps, AuthModalProps {}

const BlurryBackground = ({ listing, featureFlags }: BaseProps) => {
  const listingPhotos = listing?.photos
    ?.map(photo => photo?.file_descriptor?.urls?.cdn)
    .filter((url): url is string => !!url)
  const mainPhoto = listingPhotos?.[0]
  const verifiedUrl = hooks.useBackgroundImageWithFallback(
    mainPhoto,
    featureFlags['fe.resi.enabled-photo-enhancement']
  )

  return (
    <div
      className={cx('blurry-background')}
      style={{ backgroundImage: `url(${verifiedUrl})` }}
    />
  )
}

const OpenHouseChiclet = ({ listing, featureFlags }: BaseProps) => (
  <div onClick={() => null} className={cx('chiclet-container')}>
    <BlurryBackground listing={listing} featureFlags={featureFlags} />
    <div className={cx('open-house-dates')}>
      <Text color='gray' size='small'>
        Open House Dates
      </Text>
      <Text weight='heavy' data-elm-id='open_houses_dates' size='small'>
        {getOpenHouses(listing)}
      </Text>
    </div>
  </div>
)

interface ChicletButtonProps {
  elmId: string
  label: string
  onClick: (params: any) => void
}

const ChicletButton = ({ onClick, label, elmId }: ChicletButtonProps) => (
  <Button
    className={cx('chiclet-button')}
    elmId={elmId}
    onClick={onClick}
    role='secondary'
    size='large'
  >
    {label}
  </Button>
)

const PrivateTourChiclet = ({
  featureFlags,
  listing,
  pushOnboardingQueueItem,
  toggleAuthModal,
}: ChicletPropsWithAuth) => {
  const applicationModal = useApplicationModals()
  const { user: untypedUser } = useApplicationContext()
  const user = untypedUser as AuctionUser
  const contactModal = useContactModal({ user })

  const brokerAgent = getContactAgentInfo(listing as CombinedListingType)
  const hasPrivateAgent = Boolean(brokerAgent?.agentEmail)

  const openContactModal = () => {
    setTimeout(() => contactModal.open(), 10)
  }

  const handlePrivateTourClick = () => {
    if (hasPrivateAgent) {
      if (user.logged_in) {
        openContactModal()
      } else {
        toggleAuthModal()
        pushOnboardingQueueItem([openContactModal])
      }
    } else {
      // @ts-ignore modal types need updating, todo...
      applicationModal.open('confirmation', {
        title: 'Licensed agents only',
        children: `Please contact ${formatPhoneNumber(
          constants.contactNumbers.INTERIOR_ACCESS_NUMBER
        )} for details.`,
        confirmText: 'Close',
        hideCancelButton: true,
      })
    }
  }

  return (
    <div onClick={() => null} className={cx('chiclet-container')}>
      <BlurryBackground listing={listing} featureFlags={featureFlags} />
      <ChicletButton
        elmId='private_tour_chiclet_button'
        onClick={handlePrivateTourClick}
        label='Request a Private Tour'
      />
    </div>
  )
}

const ImportantDocumentsChiclet = ({ listing, featureFlags }: BaseProps) => (
  <div onClick={() => null} className={cx('chiclet-container')}>
    <BlurryBackground listing={listing} featureFlags={featureFlags} />
    <ScrollSpy selector='#due_diligence'>
      {({ scrollTo }: { scrollTo: any }) => (
        <ChicletButton
          elmId='view_documents_vault_chiclet_button'
          onClick={scrollTo}
          label='View Document Vault'
        />
      )}
    </ScrollSpy>
  </div>
)

const SalesAndTaxChiclet = ({
  featureFlags,
  listing,
  pushOnboardingQueueItem,
  toggleAuthModal,
  user,
}: ChicletPropsWithAuth) => {
  const { updateMarketingCardIndex } = useMarketingAnalysisCard()

  return (
    <div onClick={() => null} className={cx('chiclet-container')}>
      <BlurryBackground listing={listing} featureFlags={featureFlags} />
      <ScrollSpy selector='#comp_sales'>
        {({ scrollTo }: { scrollTo: any }) => (
          <ChicletButton
            elmId='sales_and_tax_chiclet_button'
            label='View Sales & Tax History'
            onClick={() => {
              if (!user?.logged_in) {
                toggleAuthModal()
                pushOnboardingQueueItem([scrollTo])
              } else {
                scrollTo()
              }
              updateMarketingCardIndex(2)
            }}
          />
        )}
      </ScrollSpy>
    </div>
  )
}

const BASE_ITEM_PROPS = {
  itemType: AlternateItemType.CustomItem,
  showAlways: true,
  renderer: () => {},
}

export const getAlternativeItems = ({
  isPropertyReportAvailable,
  listing,
  ...props
}: ChicletPropsWithAuth & { isPropertyReportAvailable: boolean }) => {
  const alternativeItems = []
  alternativeItems.push({
    ...BASE_ITEM_PROPS,
    itemType: AlternateItemType.MapItem,
  })

  const shouldDisplayOH = getOpenHouses(listing)?.length > 0
  const canRequestPrivateTour = Boolean(
    listing.listing_configuration?.are_private_tours_available
  )

  if (shouldDisplayOH) {
    alternativeItems.push({
      ...BASE_ITEM_PROPS,
      renderer: () => <OpenHouseChiclet listing={listing} {...props} />,
    })
  } else if (canRequestPrivateTour) {
    alternativeItems.push({
      ...BASE_ITEM_PROPS,
      renderer: () => <PrivateTourChiclet listing={listing} {...props} />,
    })
  }

  alternativeItems.push({
    ...BASE_ITEM_PROPS,
    itemType: AlternateItemType.StreetViewItem,
  })

  if (isPropertyReportAvailable)
    alternativeItems.push({
      ...BASE_ITEM_PROPS,
      showAlways: false,
      renderer: () => <SalesAndTaxChiclet listing={listing} {...props} />,
    })

  alternativeItems.push({
    ...BASE_ITEM_PROPS,
    showAlways: false,
    renderer: () => <ImportantDocumentsChiclet listing={listing} {...props} />,
  })

  return alternativeItems
}
